import axios from "axios";
import { getAccessToken, getRefreshToken, setAuthTokens } from "@/shared/auth/authTokens";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 60000,
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function readAuthTokens(payload: unknown): { access?: string; refresh?: string } {
  const root = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
  const nested =
    root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : root;
  return {
    access: typeof nested.access_token === "string" ? nested.access_token : undefined,
    refresh: typeof nested.refresh_token === "string" ? nested.refresh_token : undefined,
  };
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error?.config?.url || "";
    const isRefreshRequest = requestUrl.includes("/auth/refresh");
    const method = String(error.config?.method || "").toLowerCase();
    const isAuthRequest =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/sso-login") ||
      requestUrl.includes("/auth/kakao-login") ||
      (method === "post" && requestUrl.includes("/organization"));

    if (
      error.response?.status === 401 &&
      !error.config?._retry &&
      !isRefreshRequest &&
      !isAuthRequest
    ) {
      error.config._retry = true;
      try {
        const refreshToken = getRefreshToken();
        const refreshResponse = await axiosInstance.post("/auth/refresh", {
          ...(refreshToken ? { refresh_token: refreshToken } : {}),
        });
        const next = readAuthTokens(refreshResponse?.data);
        if (next.access) {
          setAuthTokens(next.access, next.refresh);
        }
        return axiosInstance(error.config);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
